import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface FieldConfig {
  key: string;
  label: string;
  sortable: boolean;
  filterable: boolean;
  filterType: 'text' | 'select' | 'date' | null;
  filterOptions: string[] | null;
}

export interface DataSourceConfig {
  label: string;
  description: string;
  fields: FieldConfig[];
}

export interface DataSources {
  [key: string]: DataSourceConfig;
}

export interface ReportFilter {
  field: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'greaterThan' | 'lessThan' | 'between' | 'in';
  value: string | string[];
  valueTo?: string;
}

export interface ReportPreviewConfig {
  dataSource: string;
  fields: string[];
  filters: ReportFilter[];
  groupBy?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ReportExportConfig extends ReportPreviewConfig {
  format: 'pdf' | 'excel' | 'csv';
  reportName?: string;
}

export interface FieldMeta {
  key: string;
  label: string;
}

export interface ReportPreviewResult {
  data: Record<string, any>[];
  totalRecords: number;
  limit: number;
  offset: number;
  fields: FieldMeta[];
}

export interface ExportResult {
  fileName: string;
  format: string;
  size: number;
  recordCount: number;
  downloadUrl: string;
}

export interface AggregateConfig {
  dataSource: string;
  groupByField: string;
  aggregateField?: string;
  aggregateFunction?: 'count' | 'sum' | 'avg' | 'min' | 'max';
  filters: ReportFilter[];
  limit?: number;
}

export interface ChartDataPoint {
  label: string;
  value: number;
}

export interface AggregateSummary {
  totalRecords: number;
  groupCount: number;
  sum: number;
  average: number;
  max: number;
}

export interface AggregateResult {
  chartData: ChartDataPoint[];
  summary: AggregateSummary;
  groupByLabel: string;
  aggregateLabel: string;
  aggregateFunction: string;
}

export interface AIChartConfig {
  chartType: 'bar' | 'horizontal' | 'pie';
  groupByField: string;
  aggregateFunction: 'count' | 'sum' | 'avg' | 'max' | 'min';
  aggregateField: string;
}

export interface AIReportConfig {
  dataSource: string;
  fields: string[];
  filters: ReportFilter[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  chartConfig: AIChartConfig;
  explanation: string;
}

export interface AIInsightsHighlights {
  positive: string;
  concern: string;
  trend: string;
}

export interface AIInsightsResult {
  summary: string;
  keyFindings: string[];
  recommendations: string[];
  highlights: AIInsightsHighlights;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ===== API ENDPOINTS =====
const ENDPOINTS = {
  DATA_SOURCES: '/admin/custom-reports/data-sources',
  PREVIEW: '/admin/custom-reports/preview',
  EXPORT: '/admin/custom-reports/export',
  AGGREGATE: '/admin/custom-reports/aggregate',
  AI_GENERATE_CONFIG: '/admin/custom-reports/ai/generate-config',
  AI_INSIGHTS: '/admin/custom-reports/ai/insights',
  DOWNLOAD: '/admin/reports/download',
};

// ===== SERVICE FUNCTIONS =====

export const getDataSources = async (): Promise<{
  success: boolean;
  data?: DataSources;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<DataSources>>(ENDPOINTS.DATA_SOURCES);
    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to fetch data sources';
    return { success: false, error: { message } };
  }
};

export const previewReport = async (config: ReportPreviewConfig): Promise<{
  success: boolean;
  data?: ReportPreviewResult;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<ReportPreviewResult>>(
      ENDPOINTS.PREVIEW,
      config
    );
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to preview report';
    return { success: false, error: { message } };
  }
};

export const exportReport = async (config: ReportExportConfig): Promise<{
  success: boolean;
  data?: ExportResult;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<ExportResult>>(
      ENDPOINTS.EXPORT,
      config
    );
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to export report';
    return { success: false, error: { message } };
  }
};

export const aggregateReport = async (config: AggregateConfig): Promise<{
  success: boolean;
  data?: AggregateResult;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<AggregateResult>>(
      ENDPOINTS.AGGREGATE,
      config
    );
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to aggregate report data';
    return { success: false, error: { message } };
  }
};

export const aiGenerateConfig = async (prompt: string): Promise<{
  success: boolean;
  data?: AIReportConfig;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<AIReportConfig>>(
      ENDPOINTS.AI_GENERATE_CONFIG,
      { prompt }
    );
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to generate report configuration';
    return { success: false, error: { message } };
  }
};

export const aiGenerateInsights = async (params: {
  dataSource: string;
  fields: string[];
  filters: ReportFilter[];
  sortBy?: string;
  sortOrder?: string;
  chartSummary?: AggregateSummary | null;
}): Promise<{
  success: boolean;
  data?: AIInsightsResult;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<AIInsightsResult>>(
      ENDPOINTS.AI_INSIGHTS,
      params
    );
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to generate insights';
    return { success: false, error: { message } };
  }
};

export const downloadReport = async (fileName: string): Promise<Blob | null> => {
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
    const response = await fetch(
      `${baseUrl}${ENDPOINTS.DOWNLOAD}/${fileName}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    return await response.blob();
  } catch {
    return null;
  }
};
